<template>
  <div class="media-wrap">

    <!-- Header -->
    <div class="header">
      <h3><i class="bx bx-image"></i> Media / Image Editor</h3>
      <p>Upload, replace, remove or attach link</p>
    </div>

    <!-- EMPTY STATE -->
    <div v-if="!selectedElement" class="empty-state">
      <i class="bx bx-image-alt"></i>
      <p>Please select an image or section from preview</p>
    </div>

    <!-- CONTENT -->
    <div v-else class="media-body">

      <!-- Preview -->
      <label class="label">Current Preview</label>
      <div class="preview-box">

        <!-- Upload Loading State -->
        <div v-if="isUploading" class="loading-box">
          <i class="bx bx-loader-alt bx-spin"></i>
          <span>{{ uploadMsg }}</span>
        </div>

        <!-- Image Preview -->
        <img v-else-if="previewSrc" :src="previewSrc" class="preview-img" />

        <!-- Empty State -->
        <div v-else class="preview-empty">
          <i class="bx bx-image"></i>
          <span>No image loaded</span>
        </div>

      </div>

      <!-- File Input -->
      <label class="label">Upload New Image</label>
      <input type="file" accept="image/*" @change="onFileSelect" class="file-input" />
      <!-- <div class="size-info" v-if="imgWidth && imgHeight">
      <i class="bx bx-ruler"></i>
      Recommended Size: 
      <strong>{{ imgWidth }} x {{ imgHeight }} px</strong>
    </div> -->

      <div class="size-info small" v-if="renderW && renderH">
        Recommended Size: {{ renderW }} x {{ renderH }} px
      </div>

      <!-- Link Field -->
      <label class="label">Attach Link (Href)</label>
      <input type="text" class="file-input" v-model="hrefLink" placeholder="https://example.com or #section-id"
        @input="applyHref" />

      <!-- Actions -->
      <div class="actions">
        <button class="btn-reset" @click="resetPreview">
          <i class="bx bx-history"></i> Reset
        </button>
        <button class="btn-delete" @click="removeImage">
          <i class="bx bx-trash"></i> Remove
        </button>
      </div>

    </div>
  </div>
</template>


<script scoped>

  import { uploadFile } from "@/apis/invoiceapis";
  import { apiBaseUrl } from "@/apis/appconst";
  export default {
    name: "MediaEditor",
    emits: ["updateImage", "removeElement"],
    props: { selectedElement: Object },

    data() {
      return {
        previewSrc: null,
        imgMsg: "No image loaded",
        originalSrc: null,
        isUploading: false,
        uploadMsg: "",
        imgWidth: null,
        imgHeight: null,
        renderW: null,
        renderH: null,
      };
    },

    watch: {
      selectedElement: {
        immediate: true,
        handler(el) {
          // Reset
          this.imgWidth = null;
          this.imgHeight = null;
          this.renderW = null;
          this.renderH = null;

          if (!el) {
            this.previewSrc = null;
            return;
          }

          let targetImg = null;
          let bgUrl = null;

          /* -------------------------
             1️⃣ DIRECT <img>
          --------------------------*/
          if (el.tagName === "IMG") {
            targetImg = el;
          }

          /* -------------------------
             2️⃣ BACKGROUND IMAGE ON ELEMENT
          --------------------------*/
          if (!targetImg) {
            const bg = window.getComputedStyle(el).backgroundImage;
            if (bg && bg !== "none") {
              bgUrl = bg.replace(/url\\((['"])?(.*?)\\1\\)/, "$2");
            }
          }

          /* -------------------------
             3️⃣ FIND <img> INSIDE SECTION
          --------------------------*/
          if (!targetImg && el.querySelector("img")) {
            targetImg = el.querySelector("img");
          }

          /* -------------------------
             4️⃣ FIND CHILD WITH BACKGROUND
          --------------------------*/
          if (!targetImg && !bgUrl) {
            const bgDiv = el.querySelector("[style*='background-image']");
            if (bgDiv) {
              const bg = window.getComputedStyle(bgDiv).backgroundImage;
              bgUrl = bg.replace(/url\\((['"])?(.*?)\\1\\)/, "$2");
            }
          }

          /* -------------------------
             5️⃣ PROCESS FOUND IMAGE
          --------------------------*/
          if (targetImg) {
            this.originalSrc = targetImg.src;
            this.previewSrc = targetImg.src;

            const img = new Image();
            img.src = targetImg.src;

            img.onload = () => {
              this.imgWidth = img.naturalWidth;
              this.imgHeight = img.naturalHeight;

              this.renderW = targetImg.clientWidth;
              this.renderH = targetImg.clientHeight;
            };
          }

          /* -------------------------
             6️⃣ PROCESS BACKGROUND IMAGE
          --------------------------*/
          else if (bgUrl) {
            this.originalSrc = bgUrl;
            this.previewSrc = bgUrl;

            const img = new Image();
            img.src = bgUrl;

            img.onload = () => {
              this.imgWidth = img.naturalWidth;
              this.imgHeight = img.naturalHeight;

              this.renderW = el.clientWidth;
              this.renderH = el.clientHeight;
            };
          }

          /* -------------------------
             7️⃣ NO IMAGE FOUND → SHOW ELEMENT SIZE
          --------------------------*/
          else {
            this.previewSrc = null;

            // Recommended size is simply element size
            this.imgWidth = el.clientWidth || 0;
            this.imgHeight = el.clientHeight || 0;

            this.renderW = el.clientWidth || 0;
            this.renderH = el.clientHeight || 0;
          }

          /* -------------------------
             8️⃣ HREF LINK
          --------------------------*/
          if (el.closest("a")) {
            this.hrefLink = el.closest("a").getAttribute("href") || "";
          } else {
            this.hrefLink = "";
          }
        }
      }
    }
    ,
    methods: {
      applyHref() {
        if (!this.selectedElement) return;

        let element = this.selectedElement;

        // If link exists, update it
        if (element.closest("a")) {
          element.closest("a").setAttribute("href", this.hrefLink);
          return;
        }

        // If no link, wrap element in <a>
        const wrapper = document.createElement("a");
        wrapper.href = this.hrefLink;
        wrapper.target = "_blank";
        element.replaceWith(wrapper);
        wrapper.appendChild(element);
      }
      ,
      async onFileSelect(event) {
        const file = event.target.files[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
          alert("Max allowed size is 5MB");
          return;
        }

        const fd = new FormData();
        fd.append("file1", file);

        this.isUploading = true;
        this.uploadMsg = "Uploading...";

        const res = await uploadFile(fd);

        if (res?.status && res.file1) {
          this.previewSrc = apiBaseUrl + res.file1;
          this.$emit("updateImage", { url: this.previewSrc });
          this.uploadMsg = "Uploaded Successfully";
        } else {
          this.uploadMsg = "Upload Failed!";
        }

        // remove message after delay
        setTimeout(() => {
          this.isUploading = false;
          this.uploadMsg = "";
        }, 1800);
      }
      ,

      removeImage() {
        this.previewSrc = null;
        this.$emit("updateImage", { url: null });
      },

      resetPreview() {
        this.previewSrc = this.originalSrc;
        this.$emit("updateImage", { url: this.originalSrc });
      }
    }
  };
</script>
<style scoped>
  .media-wrap {
    background: #fff;
    border: 1px solid #e5e5e5;
    border-radius: 14px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  /* Header */
  .header h3 {
    font-size: 15px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 6px;
    color: #111;
  }

  .header p {
    font-size: 12px;
    color: #888;
    margin-top: -4px;
  }

  /* Empty state */
  .empty-state {
    border: 1px dashed #cfcfcf;
    border-radius: 12px;
    padding: 28px 14px;
    text-align: center;
    color: #999;
  }

  .empty-state i {
    font-size: 22px;
    margin-bottom: 4px;
  }

  /* Body */
  .media-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  /* Labels */
  .label {
    font-size: 12px;
    color: #333;
    font-weight: 500;
  }

  /* Preview Box */
  .preview-box {
    height: 150px;
    border: 1px solid #ddd;
    border-radius: 10px;
    background: #fafafa;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    transition: .15s ease;
  }

  .preview-box:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, .08);
  }

  .preview-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .preview-empty {
    text-align: center;
    color: #9b9b9b;
    font-size: 12px;
  }

  .preview-empty i {
    font-size: 20px;
    margin-bottom: 4px;
  }

  /* File Input */
  .file-input {
    width: 100%;
    padding: 9px;
    border: 1px solid #d4d4d4;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-size: 13px;
  }

  .file-input:hover {
    border-color: #000;
  }

  /* Tool buttons */
  .tool-row {
    display: flex;
    gap: 8px;
  }

  .tool-btn {
    flex: 1;
    padding: 8px 0;
    background: #f5f5f5;
    border-radius: 8px;
    border: 1px solid #e2e2e2;
    color: #555;
    font-size: 12.5px;
    cursor: pointer;
    transition: .18s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
  }

  .tool-btn:hover {
    background: #e9e9e9;
  }

  /* Action Buttons */
  .actions {
    display: flex;
    gap: 8px;
  }

  .actions button {
    flex: 1;
    padding: 9px;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
  }

  /* Reset */
  .btn-reset {
    background: #f2f2f2;
    color: #000;
    border: 1px solid #ddd;
  }

  .btn-reset:hover {
    background: #e6e6e6;
  }

  /* Delete */
  .btn-delete {
    background: #000;
    color: #fff;
    border: 1px solid #000;
  }

  .btn-delete:hover {
    background: #222;
  }

  .loading-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #333;
    gap: 6px;
    font-size: 13px;
  }

  .loading-box i {
    font-size: 26px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  .size-info {
    margin-top: -4px;
    font-size: 12px;
    color: #444;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    padding: 6px 10px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .size-info i {
    font-size: 15px;
  }

  .size-info.small {
    margin-top: -8px;
    opacity: 0.8;
  }
</style>